import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import AuthCard from '../components/AuthCard';
import Button from '../components/Button';
import Field from '../components/Field';
import FormBanner from '../components/FormBanner';
import { errorMessage } from '../lib/errors';
import { PASSWORD_MAX_LENGTH, passwordError } from '../lib/validation';

export default function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirm?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs: { password?: string; confirm?: string } = {};
    const pErr = passwordError(password);
    if (pErr) errs.password = pErr;
    if (confirm !== password) errs.confirm = 'Passwords don’t match.';
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setFormError(null);
    setSubmitting(true);
    try {
      await api.resetPassword({ token, password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthCard
        title="Reset link invalid"
        footer={
          <Link to="/forgot" className="text-[var(--cad-accent,#818cf8)] hover:text-[var(--cad-accent,#a5b4fc)]">
            Request a new link
          </Link>
        }
      >
        <FormBanner kind="error">
          This password reset link is missing its token. Request a new one.
        </FormBanner>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Choose a new password"
      footer={
        <Link to="/login" className="text-[var(--cad-accent,#818cf8)] hover:text-[var(--cad-accent,#a5b4fc)]">
          Back to sign in
        </Link>
      }
    >
      {done ? (
        <FormBanner kind="success">Password updated. Redirecting you to sign in…</FormBanner>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          {formError ? <FormBanner kind="error">{formError}</FormBanner> : null}
          <Field
            label="New password"
            name="password"
            type="password"
            autoComplete="new-password"
            maxLength={PASSWORD_MAX_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors.password}
          />
          <Field
            label="Confirm password"
            name="confirm"
            type="password"
            autoComplete="new-password"
            maxLength={PASSWORD_MAX_LENGTH}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={fieldErrors.confirm}
          />
          <Button type="submit" loading={submitting} className="w-full">
            Update password
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
